// src/pages/Clubs/MiniClubCard.jsx
import React, { useState } from "react";
import {
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaMapMarkerAlt,
  FaUser,
  FaUsers,
  FaFacebookF,
} from "react-icons/fa";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";

const PANELS = ["Coordonnées", "Localisation", "Encadrement & disciplines"];

const MiniClubCard = ({ club, handleEditClub, handleDeleteClub }) => {
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const [panelIndex, setPanelIndex] = useState(0);

  const goPrev = () =>
    setPanelIndex((prev) => (prev + PANELS.length - 1) % PANELS.length);
  const goNext = () =>
    setPanelIndex((prev) => (prev + 1) % PANELS.length);

  const disciplinesShort =
    (club.disciplines || "").length > 60
      ? club.disciplines.slice(0, 60) + "…"
      : club.disciplines || "—";

  const socialUrl = (club.reseaux_sociaux || "").trim();
  const hasSocial = !!socialUrl;
  const isFacebook = hasSocial && socialUrl.includes("facebook.com");

  return (
    <div className="relative bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-md rounded-2xl p-4 flex flex-col gap-3 transition-all duration-150 hover:shadow-lg hover:-translate-y-0.5">
      {/* HEADER : logo + nom + commune (fixe) */}
      <div className="flex items-center gap-3">
        {/* Logo à gauche */}
        <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
          <img
            src={club.lien_logo}
            alt={`Logo de ${club.nom_club}`}
            className="max-h-16 w-auto object-contain"
            onError={(e) => {
              e.currentTarget.src = "/img/user_avatar.jpg";
            }}
          />
        </div>

        {/* Nom du club + commune */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm sm:text-base uppercase tracking-[0.06em] text-slate-800 dark:text-slate-50 line-clamp-2">
            {club.nom_club}
          </h3>
          <p className="text-[11px] mt-1 text-slate-500 dark:text-slate-300 flex items-center gap-1">
            <FaMapMarkerAlt className="inline-block" />
            <span className="truncate">{club.commune}</span>
          </p>
        </div>
      </div>

      {/* SLIDER PANELS */}
      <div className="mt-1 rounded-2xl bg-slate-50/90 dark:bg-slate-800/90 px-3 py-3 overflow-hidden">
        {/* Barre de titre + index panel */}
        <div className="flex items-center justify-between mb-2 text-[11px] font-medium text-slate-600 dark:text-slate-200">
          <span>{PANELS[panelIndex]}</span>
          <span className="text-[10px] text-slate-400 dark:text-slate-300">
            {panelIndex + 1} / {PANELS.length}
          </span>
        </div>

        {/* Conteneur slide */}
        <div className="relative w-full overflow-hidden">
          <div
            className="flex transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${panelIndex * 100}%)` }}
          >
            {/* PANEL 1 : Coordonnées */}
            <div className="w-full flex-shrink-0 text-[11px] space-y-1 pr-3">
              <div className="flex items-center gap-2">
                <FaPhone className="text-slate-500 dark:text-slate-300" />
                <span className="truncate">
                  {club.tel || "Téléphone non renseigné"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <FaEnvelope className="text-slate-500 dark:text-slate-300" />
                {club.mail ? (
                  <a
                    href={`mailto:${club.mail}`}
                    className="truncate text-primary hover:underline"
                  >
                    {club.mail}
                  </a>
                ) : (
                  <span>Email non renseigné</span>
                )}
              </div>
              <div className="flex items-start gap-2">
                <FaGlobe className="mt-[2px] text-slate-500 dark:text-slate-300" />
                {club.site_internet ? (
                  <a
                    href={club.site_internet}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate text-primary hover:underline"
                  >
                    Site internet
                  </a>
                ) : (
                  <span>Site internet non renseigné</span>
                )}
              </div>
              {hasSocial && (
                <div className="flex items-center gap-2 mt-1">
                  {isFacebook ? (
                    <a
                      href={socialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1877F2] text-white text-xs hover:scale-105 transition-transform"
                      title="Page Facebook"
                    >
                      <FaFacebookF />
                    </a>
                  ) : (
                    <a
                      href={socialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs hover:scale-105 transition-transform"
                      title="Réseaux sociaux"
                    >
                      <FaGlobe />
                    </a>
                  )}
                  <span className="text-[11px]">
                    {isFacebook ? "Page Facebook" : "Réseaux sociaux"}
                  </span>
                </div>
              )}
            </div>

            {/* PANEL 2 : Localisation */}
            <div className="w-full flex-shrink-0 text-[11px] space-y-1 pr-3">
              <div className="flex items-start gap-2">
                <FaMapMarkerAlt className="mt-[2px] text-slate-500 dark:text-slate-300" />
                <div>
                  <p className="font-semibold">Adresse :</p>
                  <p className="break-words">
                    {club.adresse || "Adresse non renseignée"}
                  </p>
                </div>
              </div>
              {club.secteur && (
                <p>
                  <span className="font-semibold">Secteur :</span>{" "}
                  {club.secteur}
                </p>
              )}
              {club.secteur_geo && (
                <p>
                  <span className="font-semibold">Secteur géographique :</span>{" "}
                  {club.secteur_geo}
                </p>
              )}
              {club.numero_club && (
                <p>
                  <span className="font-semibold">N° club :</span>{" "}
                  {club.numero_club}
                </p>
              )}
              {club.lien_maps && (
                <p className="mt-1">
                  <a
                    href={club.lien_maps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Voir sur Google Maps
                  </a>
                </p>
              )}
            </div>

            {/* PANEL 3 : Encadrement & disciplines */}
            <div className="w-full flex-shrink-0 text-[11px] space-y-1 pr-3">
              <div className="flex items-center gap-2">
                <FaUser className="text-slate-500 dark:text-slate-300" />
                <span>
                  <span className="font-semibold">Président :</span>{" "}
                  {club.president || "Non renseigné"}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <FaUsers className="mt-[2px] text-slate-500 dark:text-slate-300" />
                <span>
                  <span className="font-semibold">Enseignant(s) :</span>{" "}
                  {club.enseignant || "Non renseigné"}
                </span>
              </div>
              <div className="mt-1">
                <p className="font-semibold mb-0.5">Disciplines :</p>
                <p className="break-words">{disciplinesShort}</p>
              </div>
            </div>
          </div>
        </div>

        {/* NAV + BULLETS EN BAS */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={goPrev}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-100 text-xs hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
          >
            <FaChevronLeft />
          </button>

          <div className="flex justify-center gap-1">
            {PANELS.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setPanelIndex(idx)}
                className={`h-1.5 rounded-full transition-all ${
                  panelIndex === idx
                    ? "w-4 bg-primary"
                    : "w-2 bg-slate-300 dark:bg-slate-600"
                }`}
                aria-label={`Aller au panneau ${idx + 1}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={goNext}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-100 text-xs hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
          >
            <FaChevronRight />
          </button>
        </div>
      </div>

      {/* Boutons admin, plus petits & modernes */}
      {(userRole === "Administrateur" || userRole === "Bureau") && (
        <div className="flex justify-center space-x-2 mt-1">
          <button
            type="button"
            onClick={() => handleEditClub(club)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-100 text-xs hover:bg-primary hover:text-white transition-colors"
            title="Modifier le club"
          >
            <FaEdit />
          </button>
          <button
            type="button"
            onClick={() => handleDeleteClub(club.id)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-red-300 dark:border-red-600 bg-white dark:bg-slate-800 text-red-500 dark:text-red-300 text-xs hover:bg-red-500 hover:text-white transition-colors"
            title="Supprimer le club"
          >
            <FaTrash />
          </button>
        </div>
      )}
    </div>
  );
};

export default MiniClubCard;
